import { forwardRef, useImperativeHandle, useRef, type ReactNode } from "react";

import { cx } from "../lib/cx";

/** `markdown-dialect.md` §15: a title is at most 100 characters. */
export const TITLE_MAX_LENGTH = 100;

export interface TitleInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Enter (and ↓ at the end) move into the body (`editor.md` §12 heading). */
  onEnter?: () => void;
  placeholder?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  autoFocus?: boolean;
}

export interface TitleInputHandle {
  focus: () => void;
  select: () => void;
}

/**
 * The document title is a separate field, not the first block of the body: it
 * has its own length cap, it never takes formatting, and Enter belongs to the
 * body rather than to a new paragraph here. A textarea gives it wrapping
 * without contenteditable's markup surprises.
 */
export const TitleInput = forwardRef<TitleInputHandle, TitleInputProps>(function TitleInput(
  {
    value,
    onChange,
    onEnter,
    placeholder = "Untitled",
    readOnly = false,
    fullWidth = false,
    autoFocus = false,
  },
  ref,
): ReactNode {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    select: () => inputRef.current?.select(),
  }));

  return (
    <textarea
      ref={inputRef}
      className={cx("wiki-title", fullWidth && "wiki-title--full-width")}
      aria-label="Document title"
      placeholder={placeholder}
      rows={1}
      maxLength={TITLE_MAX_LENGTH}
      readOnly={readOnly}
      autoFocus={autoFocus}
      value={value}
      onChange={(event) => {
        // Paste of a multi-line title collapses: a title is one line.
        onChange(event.target.value.replace(/[\n\r]+/g, " ").slice(0, TITLE_MAX_LENGTH));
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || (event.key === "ArrowDown" && !event.shiftKey)) {
          if (event.key === "Enter") event.preventDefault();
          onEnter?.();
        }
      }}
    />
  );
});
